import { PageHeader, Stat, DataTable } from '../../components/office';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Notice } from '@verlo/shared/components/ui/notice';
import Person from '../../components/people/Person';
import { Button } from '@verlo/shared/components/ui/button';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@verlo/shared/components/ui/dialog';
import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import { SearchField } from '@verlo/shared/components/ui/search-field';
import { roleLabel, RANK } from './TeamPage';

// Who can sign in to the back office with a password (Oct 2026). Read-only for roles: a
// person's role, their till PIN and their invite are on Team, on the person, which is where
// someone is added. Here: who has a back-office login, ending their sessions, resetting
// their two-factor, and switching the login off (their till PIN keeps working).
// `navigator.clipboard` can reject (no permission, insecure context) rather than throw
// synchronously: report honestly instead of assuming the copy landed.
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const AccountsPage = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [resent, setResent] = useState(null);       // { email, temporaryPassword } when no email went out
  const [confirm, setConfirm] = useState(null);     // { kind: 'mfa' | 'off', user }
  const [busy, setBusy] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await authApi.listUsers();
      setUsers(res.data);
    } catch {
      toast.error("Couldn't load accounts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchUsers(); }, []);

  const resendInvite = async (email) => {
    try {
      const res = await authApi.inviteResend(email);
      if (res.data.temporary_password) setResent({ email, temporaryPassword: res.data.temporary_password });
      else toast.success(`Invitation resent to ${email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend the invite');
    }
  };

  const signOutEverywhere = async (u) => {
    try {
      const { data } = await authApi.adminRevokeUserSessions(u.id);
      toast.success(data.ended ? `${u.name} is signed out of ${data.ended} ${data.ended === 1 ? 'device' : 'devices'}` : `${u.name} wasn't signed in anywhere`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't sign them out");
    }
  };

  const switchOn = async (u) => {
    try {
      await authApi.switchBackOffice(u.id, true);
      toast.success(`${u.name} can sign in to the back office again`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't switch it on");
    }
  };

  const confirmIt = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'mfa') {
        await authApi.adminResetMfa(confirm.user.id);
        toast.success(`Two-factor removed for ${confirm.user.name}`);
      } else {
        await authApi.switchBackOffice(confirm.user.id, false);
        toast.success(`${confirm.user.name}'s back-office login is off. Their till PIN still works.`);
        fetchUsers();
      }
      setConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't do that");
    } finally {
      setBusy(false);
    }
  };

  // What you can do to an account: only someone above them, and nothing to your own here.
  const manage = (u) => {
    if (!u.may_manage || !u.is_active) return null;
    return (
      <div className="flex items-center justify-end gap-1">
        {u.must_set_password && !u.back_office_off && (
          <Button variant="ghost" size="sm" onClick={() => resendInvite(u.email)} className="text-ink-soft" data-testid={`resend-invite-${u.id}`}>Resend the invite</Button>
        )}
        {!u.must_set_password && !u.back_office_off && (
          <>
            <Button variant="ghost" size="sm" onClick={() => signOutEverywhere(u)} className="text-ink-soft" data-testid={`account-sign-out-${u.id}`}>Sign out everywhere</Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirm({ kind: 'mfa', user: u })} className="text-ink-soft" data-testid={`account-reset-mfa-${u.id}`}>Reset two-factor</Button>
          </>
        )}
        {u.back_office_off ? (
          <Button variant="ghost" size="sm" onClick={() => switchOn(u)} className="text-ink-soft" data-testid={`account-switch-on-${u.id}`}>Switch on</Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => setConfirm({ kind: 'off', user: u })} className="text-signal-stop hover:text-signal-stop" data-testid={`account-switch-off-${u.id}`}>Switch off</Button>
        )}
      </div>
    );
  };

  const filtered = users.filter((u) =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const pendingCount = users.filter((u) => u.must_set_password).length;
  const offCount = users.filter((u) => u.back_office_off).length;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <PageHeader
        title="Accounts"
        purpose="Who can sign in to the back office with a password, and where they're signed in."
      />

      <Notice tone="info" word="Team" title="People are added on Team"
        action={<Button asChild variant="outline" size="sm"><Link to="/back-office/team" data-testid="accounts-to-team">Open Team</Link></Button>}>
        Open someone on Team to set their role, send them a till PIN, or invite them to the back office.
      </Notice>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Stat label="Accounts" value={users.length} />
        <Stat label="Invite pending" value={pendingCount} />
        {offCount > 0 && <Stat label="Switched off" value={offCount} />}
      </div>

      <SearchField className="max-w-sm" placeholder="Search accounts..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="accounts-search" />

      {/* One column each, the same on every row, like Team (Chris, Oct 2026). The buttons are
          always shown, not on hover: there are only ever a handful of accounts. */}
      <DataTable
        testId="accounts-list"
        rowTestId={(u) => `account-${u.id}`}
        columns={[
          { key: 'name', label: `${filtered.length} ${filtered.length === 1 ? 'account' : 'accounts'}`, sortable: true, render: (u) => (
            <Person name={u.name} role={u.email}
              after={u.back_office_off ? <Signal tone="stop" dot={false}>switched off</Signal>
                : u.must_set_password && <Signal tone="warn" dot={false}>invite pending</Signal>} />) },
          { key: 'rbac_role', label: 'Role', muted: true, sortable: true, className: 'w-40 whitespace-nowrap', sortValue: (u) => -(RANK[u.rbac_role] ?? 0), render: (u) => (
            <span data-testid={`account-role-${u.id}`}>{roleLabel(u.rbac_role)}{u.id === me?.id ? ' · you' : ''}</span>) },
          { key: 'created_at', label: 'Since', muted: true, sortable: true, className: 'hidden w-40 whitespace-nowrap 2xl:table-cell', render: (u) => `${u.must_set_password ? 'Invited' : 'Joined'} ${formatDate(u.created_at)}` },
          { key: 'manage', label: '', className: 'w-px whitespace-nowrap', align: 'right', render: manage },
        ]}
        rows={filtered}
        rowKey="id"
        empty={{ word: 'Accounts', title: searchQuery ? 'Nobody matches' : 'No accounts yet', children: 'Invite someone from Team.' }}
      />

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="account-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{confirm?.kind === 'mfa' ? `Reset two-factor for ${confirm?.user.name}` : `Switch off ${confirm?.user.name}'s back-office login`}</DialogTitle>
            <DialogDescription>
              {confirm?.kind === 'mfa'
                ? 'For someone who has lost their phone. This removes their authenticator app and signs them out everywhere. They can set up a new one from My settings.'
                : 'They are signed out of the back office everywhere, and their password stops working until it is switched back on. Their till PIN keeps working. To take them off the tills too, open them on Team and choose “They’ve left”.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={confirmIt} disabled={busy} data-testid="account-confirm">
              {busy ? 'Working…' : confirm?.kind === 'mfa' ? 'Reset two-factor' : 'Switch it off'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resent} onOpenChange={(o) => { if (!o) setResent(null); }}>
        <DialogContent className="max-w-sm" data-testid="invite-dialog">
          <DialogHeader><DialogTitle>Temporary password</DialogTitle></DialogHeader>
          <p className="text-sm text-ink-soft">
            Email isn't set up here, so give <span className="font-medium text-ink">{resent?.email}</span> this temporary
            password yourself. It won't be shown again. They'll choose their own when they first sign in, and it stops
            working after 7 days.
          </p>
          <div className="flex items-center justify-between gap-2 rounded-lg border border-hairline bg-surface-2 px-3 py-2.5">
            <span className="font-mono text-sm text-ink" data-testid="invite-temporary-password">{resent?.temporaryPassword}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-ink-soft" title="Copy"
              onClick={async () => {
                const copied = await copyToClipboard(resent.temporaryPassword);
                toast[copied ? 'success' : 'error'](copied ? 'Copied' : "Couldn't copy. Note it down.");
              }}>
              <Copy size={14} />
            </Button>
          </div>
          <DialogFooter><Button className="bg-verlo hover:bg-verlo-hover" onClick={() => setResent(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsPage;
